import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listSessions, createSession, listCompanies } from '../api/sessions';
import { useAppStore } from '../stores/app';

export function SessionSelector() {
  const activeId = useAppStore((s) => s.activeSessionId);
  const setActive = useAppStore((s) => s.setActiveSession);
  const queryClient = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => listSessions({ limit: 50 }),
  });

  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: () => listCompanies(),
  });

  const createMutation = useMutation({
    mutationFn: (title: string) => {
      const companyId = companiesData?.data?.[0]?.id;
      if (!companyId) throw new Error('Kein Unternehmen vorhanden');
      return createSession({ title, company_id: companyId });
    },
    onSuccess: (session) => {
      queryClient.invalidateQueries({ queryKey: ['sessions'] });
      setActive(session.id);
      setShowNew(false);
      setNewTitle('');
    },
  });

  const sessions = data?.data ?? [];

  return (
    <div className="flex items-center gap-2">
      <label htmlFor="session-select" className="text-xs font-medium text-gray-500">
        Session:
      </label>
      <select
        id="session-select"
        value={activeId ?? ''}
        onChange={(e) => setActive(e.target.value || null)}
        disabled={isLoading}
        className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:ring-2 focus:ring-brand-500 focus:border-transparent"
      >
        <option value="">— Wähle Session —</option>
        {sessions.map((s) => (
          <option key={s.id} value={s.id}>
            {s.title}
          </option>
        ))}
      </select>

      {!showNew ? (
        <button
          type="button"
          onClick={() => setShowNew(true)}
          className="text-xs px-2 py-1 rounded bg-brand-600 text-white hover:bg-brand-700 transition-colors"
        >
          + Neue Session
        </button>
      ) : (
        <form
          className="flex items-center gap-1"
          onSubmit={(e) => {
            e.preventDefault();
            if (newTitle.trim()) createMutation.mutate(newTitle.trim());
          }}
        >
          <input
            autoFocus
            type="text"
            placeholder="Session-Name…"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            className="text-sm border border-gray-300 rounded-md px-2 py-1 w-44 focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
          <button
            type="submit"
            disabled={createMutation.isPending || !newTitle.trim()}
            className="text-xs px-2 py-1 rounded bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 transition-colors"
          >
            {createMutation.isPending ? '…' : 'OK'}
          </button>
          <button
            type="button"
            onClick={() => { setShowNew(false); setNewTitle(''); }}
            className="text-xs px-2 py-1 rounded text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
          {createMutation.isError && (
            <span className="text-xs text-red-600">
              {(createMutation.error as Error).message}
            </span>
          )}
        </form>
      )}
    </div>
  );
}
